import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Progress, Typography, Upload, message } from "antd";
import { CaretRightOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { VolunteersGalleryRules } from "../../context/VolunteersGalleryContext/constants";
import { checkGalleryFile, formatFileSize, GALLERY_ACCEPT, getGalleryMediaKind } from "../../utils/galleryMedia";
import { FileName, FileSize, PickerContainer, SelectedFile, SelectedFiles, Thumbnail, VideoBadge } from "./styles";

interface GalleryMediaPickerProps {
	title: string;
	files: File[];
	onChange: (files: File[]) => void;
	isDisabled?: boolean;
	// 0-100 while the files are being uploaded.
	uploadProgress?: number | null;
}

const FORMAT_LIST = [...VolunteersGalleryRules.pictureExtensions, ...VolunteersGalleryRules.videoExtensions].join(", ").toUpperCase();

// One preview address per picked file for as long as the file stays picked: a new address for every change would
// reload every thumbnail, and an address revoked while its video is still loading fails in WebKit.
const usePreviewUrls = (files: File[]) => {
	const urlsRef = useRef(new Map<File, string>());
	const urls = useMemo(() => new Map(files.map(file => [file, urlsRef.current.get(file) ?? URL.createObjectURL(file)])), [files]);

	useEffect(() => {
		urlsRef.current.forEach((url, file) => {
			if (!urls.has(file)) {
				URL.revokeObjectURL(url);
			}
		});
		urlsRef.current = urls;
	}, [urls]);

	useEffect(() => () => urlsRef.current.forEach(url => URL.revokeObjectURL(url)), []);

	return urls;
};

// Picks the pictures and videos of a gallery before they are uploaded. It looks like FileUploadComponent, which takes
// one size limit for every file; a gallery needs separate picture and video limits, MP4 files and content checks
// before a file is accepted. Every picked file is checked at once (format, size, content); the ones that fail are
// reported and left out.
const GalleryMediaPicker = ({ title, files, onChange, isDisabled = false, uploadProgress = null }: GalleryMediaPickerProps) => {
	const [isChecking, setIsChecking] = useState(false);
	const previews = usePreviewUrls(files);

	const addFiles = async (picked: File[]) => {
		const room = VolunteersGalleryRules.maxFileCount - files.length;
		const selectedNames = new Set(files.map(file => file.name));
		const duplicates = picked.filter(file => selectedNames.has(file.name));
		const candidates = picked.filter(file => !selectedNames.has(file.name));
		const toCheck = candidates.slice(0, Math.max(room, 0));

		duplicates.forEach(file => message.error(`${file.name} adlı dosya zaten seçili.`));
		if (candidates.length > toCheck.length) {
			message.warning(`En fazla ${VolunteersGalleryRules.maxFileCount} dosya seçebilirsiniz; ${candidates.length - toCheck.length} dosya eklenmedi.`);
		}
		if (toCheck.length === 0) {
			return;
		}

		setIsChecking(true);
		const checks = await Promise.all(toCheck.map(checkGalleryFile));
		setIsChecking(false);

		checks.filter(check => check.error).forEach(check => message.error(`${check.file.name} dosyası eklenemedi: ${check.error}`));
		const accepted = checks.filter(check => !check.error).map(check => check.file);
		if (accepted.length > 0) {
			onChange([...files, ...accepted]);
			message.success(accepted.length === 1 ? `${accepted[0].name} dosyası eklendi.` : `${accepted.length} dosya eklendi.`);
		}
	};

	const removeFile = (file: File) => onChange(files.filter(item => item !== file));

	return (
		<PickerContainer>
			<Typography.Title level={5} style={{ color: "#2e333e" }}>
				{title}
			</Typography.Title>
			<Typography.Paragraph>
				Desteklenen formatlar: {FORMAT_LIST}
				<br />
				Maksimum dosya boyutu: görsel {formatFileSize(VolunteersGalleryRules.pictureMaxFileSize)}, video {formatFileSize(VolunteersGalleryRules.videoMaxFileSize)}
				<br />
				Maksimum dosya adedi: {VolunteersGalleryRules.maxFileCount}
			</Typography.Paragraph>
			<Upload
				beforeUpload={(file, batch) => {
					// Called once per file; the whole selection is checked once, in its order.
					if (file === batch[0]) {
						addFiles(batch);
					}
					return false;
				}}
				disabled={isDisabled || isChecking}
				fileList={[]}
				showUploadList={false}
				multiple
				accept={GALLERY_ACCEPT}
			>
				<Button icon={<UploadOutlined />} size="large" disabled={isDisabled} loading={isChecking}>
					Dosya Seçiniz
				</Button>
			</Upload>

			{files.length > 0 && (
				<>
					<SelectedFiles aria-label="Seçilen dosyalar">
						{files.map(file => {
							const url = previews.get(file);
							const isVideo = getGalleryMediaKind(file.name) === "video";
							return (
								<SelectedFile key={file.name}>
									<Thumbnail>
										{isVideo ? <video src={`${url}#t=0.1`} preload="metadata" muted aria-hidden="true" /> : <img src={url} alt={`Önizleme: ${file.name}`} />}
										{isVideo && (
											<VideoBadge aria-hidden="true">
												<CaretRightOutlined />
											</VideoBadge>
										)}
									</Thumbnail>
									<FileName title={file.name}>{file.name}</FileName>
									<FileSize>{formatFileSize(file.size)}</FileSize>
									<Button
										icon={<DeleteOutlined />}
										type="primary"
										danger
										disabled={isDisabled || isChecking}
										onClick={() => removeFile(file)}
										aria-label={`${file.name} dosyasını sil`}
									>
										Dosyayı Sil
									</Button>
								</SelectedFile>
							);
						})}
					</SelectedFiles>
					<Button
						icon={<DeleteOutlined />}
						danger
						disabled={isDisabled || isChecking}
						onClick={() => onChange([])}
						style={{ marginTop: 16 }}
						aria-label={`Tüm dosyaları sil (${files.length} dosya)`}
					>
						Tümünü Sil
					</Button>
				</>
			)}

			{/* Under the picked files, next to the save button the user has just pressed. */}
			{uploadProgress !== null && <Progress percent={uploadProgress} style={{ marginTop: 16 }} />}
		</PickerContainer>
	);
};

export default GalleryMediaPicker;
